/**
 * Marine domain pure helpers — catalog-row predicates, line formatting, and
 * boat-attribute mapping.
 *
 * These live together because they share one property: no dependencies, so the
 * Node ESM harness can import and test them directly. The HTTP layer that
 * fetches boat reference data lives in `src/services/marineReferenceService.js`,
 * which imports HTTPService and therefore cannot be harness-tested.
 *
 * A marine service is an ORDINARY installer_services price row whose
 * `vehicle_size` is the literal 'SF' and whose `service_cost` is a
 * PER-SQUARE-FOOT RATE rather than a job price. The payload is otherwise
 * shape-identical to a car service, which makes the distinction easy to lose —
 * so it lives here and nowhere else.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/marineService.test.mjs`. Do not add imports.
 */

/** The literal `vehicle_size` value the server uses for area-priced rows. */
export const MARINE_VEHICLE_SIZE = 'SF';

/**
 * The catalog payload carries `asset_type: "marine"` as of server commit
 * 137fd36. `asset_type` WINS when present, so the older `vehicle_size === 'SF'`
 * sentinel can be retired later without touching a single call site.
 *
 * A stored/refetched LINE row (`GET /requestService/:requestId`,
 * `GET /requestService/technician/:id`) is not guaranteed to carry the catalog
 * fields above, but it does carry the persisted `request_services.pricing_kind`
 * ENUM('unit','area') column — `'area'` is used ONLY for area-priced marine
 * lines. Accept both `pricing_kind` and camelCase `pricingKind`, since the
 * codebase sends either spelling depending on the call site.
 */
export function isMarineService(svc) {
  if (!svc) return false;
  if (svc.asset_type) return String(svc.asset_type).toLowerCase() === 'marine';
  if (String(svc.vehicle_size ?? '').trim().toUpperCase() === MARINE_VEHICLE_SIZE) return true;
  const pricingKind = svc.pricing_kind ?? svc.pricingKind;
  return String(pricingKind ?? '').trim().toLowerCase() === 'area';
}

/**
 * The per-sq-ft rate for a marine row; null for anything else.
 *
 * `service_cost` CANNOT be trusted here. It carries the per-sq-ft rate on a
 * CATALOG row but the LINE TOTAL on a confirmed estimate line — the calculator
 * overwrites it on purpose (`newServiceForm.jsx:766`), because
 * calculateServiceTotal, discounts and the final amount all read it. The rate
 * survives on `unit_price`.
 *
 * So read `unit_price` first, exactly as every other line-price read in the
 * codebase does (`newServiceForm.jsx:1159`, `displayJob.js:212`), and fall back
 * to `service_cost` for catalog rows, which have no `unit_price`. Getting this
 * backwards printed "220 sq ft @ $1870.00/sq ft" — the total, labelled a rate.
 *
 * Zero/blank `unit_price` falls through rather than reporting $0.00/sq ft.
 */
export function getMarineRate(svc) {
  if (!isMarineService(svc)) return null;
  const unit = Number(svc.unit_price);
  if (Number.isFinite(unit) && unit > 0) return unit;
  const rate = Number(svc.service_cost ?? svc.base_price);
  return Number.isFinite(rate) ? rate : null;
}

/** "540 sq ft" — quantity on a marine line is always whole square feet. */
export function formatArea(quantity) {
  const n = Number(quantity);
  if (!Number.isFinite(n)) return '';
  return `${n.toLocaleString('en-US')} sq ft`;
}

/** "540 sq ft @ $5.00/sq ft" — the breakdown shown beside a marine line. */
export function formatMarineLine(line) {
  if (!isMarineService(line)) return '';
  const rate = getMarineRate(line);
  if (rate == null) return formatArea(line?.quantity);
  return `${formatArea(line?.quantity)} @ $${rate.toFixed(2)}/sq ft`;
}

// ---------------------------------------------------------------------------
// Boat attributes — mapping a /api/boats/years row onto the vehicle payload
// ---------------------------------------------------------------------------

/**
 * Build the `marine_attributes` satellite payload from a chosen /years row.
 *
 * `hull_height_in` is null for EVERY feed-supplied row — no feed carries
 * freeboard. Carry the null through faithfully rather than substituting a
 * default: the area calculator prompts for it, and what the estimator enters is
 * learned back onto the row server-side.
 *
 * Emits `boat_model_uuid` + `boat_model_source` — the VENDOR identifiers, never
 * the server's internal row id, so a reference-table reload cannot silently
 * re-point a saved boat.
 */
export function buildMarineAttributes(yearRow, options) {
  const row = yearRow || {};
  const hin = options && options.hin;
  return {
    boat_class: row.boat_class ?? null,
    loa_ft: row.loa_ft ?? null,
    beam_ft: row.beam_ft ?? null,
    hull_height_in: row.hull_height_in ?? null,
    hull_material: row.hull_code ?? null,
    boat_model_uuid: row.boat_model_uuid ?? null,
    boat_model_source: row.boat_model_source ?? 'vehdb',
    hin: hin ? String(hin).trim().toUpperCase() : null,
  };
}

/**
 * The marine fields the server reads. `Vehicle.extractMarineAttributes` takes
 * these off the TOP LEVEL of the request body and never descends into a nested
 * object, so a `marine_attributes: {...}` payload yields null for every one of
 * them and the boat is written as having supplied no marine data at all.
 */
export const MARINE_WIRE_FIELDS = [
  'boat_class', 'loa_ft', 'beam_ft', 'hull_height_in', 'hull_material',
  'engine_hours', 'category', 'hin', 'boat_model_source', 'boat_model_uuid',
];

/**
 * Read a vehicle's marine attributes from EITHER shape — nested under
 * `marine_attributes` (what the wizard builds) or flat on the vehicle itself
 * (what the server stores, and what a re-fetched row looks like).
 *
 * Every consumer goes through this rather than reaching for `.marine_attributes`
 * directly, so a saved boat loaded back from the API behaves like a fresh one.
 */
export function marineAttrsOf(vehicle) {
  const v = vehicle || {};
  const nested = v.marine_attributes;
  if (nested && typeof nested === 'object') return nested;
  const flat = {};
  MARINE_WIRE_FIELDS.forEach((key) => {
    if (v[key] !== undefined && v[key] !== null) flat[key] = v[key];
  });
  return flat;
}

/**
 * The freeboard an estimator entered into the area calculator, read back off the
 * confirmed marine lines.
 *
 * No feed carries hull height, so `boat_model.hull_height_in` starts NULL and
 * the calculator is the only place the number is ever captured. The server
 * learns it back onto the reference row from `POST /vehicles` — but only when
 * `hull_height_in` and `boat_model_uuid` both arrive — so this is what closes
 * the loop. Without it every estimator re-measures the same boat forever.
 *
 * First confirmed line wins. Two marine lines on one estimate describe the same
 * hull, so they cannot legitimately disagree; if they do, the first is as good
 * a choice as any and picking deterministically beats picking the larger.
 */
export function learnedHullHeight(services) {
  if (!Array.isArray(services)) return null;
  for (const svc of services) {
    if (!svc || !isMarineService(svc)) continue;
    const vars = svc.marine_variables || svc.marineVariables;
    if (!vars || typeof vars !== 'object') continue;
    const h = Number(vars.hull_height_in);
    if (Number.isFinite(h) && h > 0) return h;
  }
  return null;
}

/**
 * Shape a vehicle for `POST /vehicles`: marine attributes FLATTENED onto the top
 * level, nested key removed.
 *
 * Pass the estimate's `services` to backfill a freeboard the estimator entered
 * in the calculator. Only fills a GAP — a hull height already on the reference
 * row always wins, so this can never overwrite known data with a typed guess.
 *
 * The client keeps them grouped under `marine_attributes` because that is a
 * useful unit to pass around (the calculator reads it as one object). The server
 * wants them flat. This is the one place those two shapes meet — flatten at the
 * boundary rather than in the wizard, so there is a single tested chokepoint
 * instead of a convention every call site has to remember.
 *
 * Non-marine vehicles pass through untouched.
 */
export function toVehiclePayload(vehicle, services) {
  const v = vehicle || {};
  const attrs = v.marine_attributes;
  if (!attrs || typeof attrs !== 'object') return { ...v };
  const { marine_attributes, ...rest } = v;
  const flat = { ...rest };
  MARINE_WIRE_FIELDS.forEach((key) => {
    if (attrs[key] !== undefined) flat[key] = attrs[key];
  });
  if (flat.hull_height_in == null) {
    const learned = learnedHullHeight(services);
    if (learned != null) flat.hull_height_in = learned;
  }
  return flat;
}

/**
 * HIN validation is ADVISORY ONLY — returns a warning string or null, and must
 * never block submission. 12 chars, [A-Z0-9], blank allowed.
 *
 * Deliberately does NOT use `src/components/common/validateVIN.jsx`: that
 * applies a 17-character VIN check digit, which a HIN does not have.
 *
 * NOT CURRENTLY CALLED — and that is intentional, not dead code. HIN is captured
 * at CHECK-IN, where the boat is physically present, not at estimate time
 * (MARINE_ASSETS_SPEC §3). This is kept, with tests, for the check-in step.
 */
export function hinWarning(hin) {
  const h = String(hin ?? '').trim().toUpperCase();
  if (h === '') return null;
  if (!/^[A-Z0-9]{12}$/.test(h)) {
    return 'HIN is usually 12 characters (letters and numbers).';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Catalog fetch params
// ---------------------------------------------------------------------------

/**
 * The size/asset filter block shared by the three Step-3 catalog URL builders.
 *
 * A marine price row is keyed `vehicle_size = 'SF'` and a boat has no body
 * style, so the automotive bodyStyle-or-vehicleSize pair does not apply.
 * Sending it anyway produced the literal string `vehicleSize=undefined`,
 * because URLSearchParams stringifies `undefined` rather than omitting it —
 * which is why this is tested rather than inlined.
 */
export function catalogFilterParams(params) {
  const p = params || {};
  const assetType = p.assetType || 'auto';
  if (assetType === 'marine') {
    return { assetType, vehicleSize: MARINE_VEHICLE_SIZE };
  }
  return {
    ...(p.bodyStyle
      ? { bodyStyle: p.bodyStyle }
      : { vehicleSize: p.vehicleSize }),
    assetType,
  };
}

/**
 * Is a boat complete enough to move past the vehicle step?
 *
 * The automotive gate requires trim, body and id_car_trim, because the car
 * service catalog is keyed on body style. A boat has none of those — the marine
 * catalog is keyed on assetType + vehicle_size 'SF' — so requiring them blocks
 * the Next button forever.
 *
 * Require instead what marine pricing actually consumes: identity for display,
 * `loa_ft` for every area formula, and `boat_class`, which gates the hull and
 * glass priors.
 */
export function isMarineVehicleComplete(vehicle) {
  const v = vehicle || {};
  const attrs = marineAttrsOf(v);
  return Boolean(
    v.year &&
    v.make &&
    v.model &&
    attrs.boat_class &&
    attrs.loa_ft
  );
}
